import React, { useState, useRef, useEffect } from 'react';

/* DOCUMENT SCANNER COMPONENT - File upload and text extraction interface */
/* Used on Upload page to scan PDFs/images and extract event text */
/* Shows file picker, upload progress, and extraction status */
const DocumentScanner = ({ onTextExtracted, API_KEY }) => {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  /* AUTO-OPEN FILE DIALOG - Opens file picker automatically on page load */
  /* Improves UX by eliminating extra click to start upload */
  useEffect(() => {
    // Give a short delay to ensure component is fully rendered
    const timer = setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }, 200);
    
    return () => clearTimeout(timer);
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check if file is a PDF or an image
      if (selectedFile.type === 'application/pdf' || 
          selectedFile.type.startsWith('image/')) {
        setFile(selectedFile);
        setError('');
      } else {
        setError('Please upload a PDF or image file (JPEG, PNG, etc.)');
        setFile(null);
      }
    }
  };

  const extractTextFromDocument = async () => {
    if (!file) {
      setError('Please select a PDF or image file first');
      return;
    }

    setIsLoading(true);
    setError('');
    setProgress(10);

    try {
      // Process based on file type
      if (file.type === 'application/pdf') {
        // Convert PDF to images
        const images = await convertPDFToImages(file);
        setProgress(30);
        
        // Extract text from images using GPT-4 Vision
        const extractedText = await processImagesWithGPT4Vision(images);
        setProgress(90);
        
        // Pass the extracted text to the parent component with source information
        if (extractedText && extractedText.trim()) {
          // Add document source metadata to the text
          const textWithSource = `${extractedText}\n\n__SOURCE:DOCUMENT__`;
          onTextExtracted(textWithSource);
          clearFileInput();
        } else {
          setError('No text could be extracted from the PDF');
        }
      } else if (file.type.startsWith('image/')) {
        // Process image directly
        setProgress(20);
        const imageDataUrl = await convertImageToDataUrl(file);
        setProgress(40);
        
        // Extract text from image using GPT-4 Vision
        const extractedText = await processImagesWithGPT4Vision([imageDataUrl]);
        setProgress(90);
        
        // Pass the extracted text to the parent component with source information
        if (extractedText && extractedText.trim()) {
          // Add document source metadata to the text
          const textWithSource = `${extractedText}\n\n__SOURCE:DOCUMENT__`;
          onTextExtracted(textWithSource);
          clearFileInput();
        } else {
          setError('No text could be extracted from the image');
        }
      }
    } catch (err) {
      console.error('Error scanning document:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  const clearFileInput = () => {
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFile(null);
  };

  const convertImageToDataUrl = (imageFile) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = function(event) {
        resolve(event.target.result);
      };
      
      reader.onerror = function(error) {
        reject(error);
      };
      
      reader.readAsDataURL(imageFile);
    });
  };

  const convertPDFToImages = async (pdfFile) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async function(event) {
        const typedArray = new Uint8Array(event.target.result);
        
        try {
          // Use PDF.js to render PDF pages as images
          const pdf = await window.pdfjsLib.getDocument({data: typedArray}).promise;
          const numPages = pdf.numPages;
          const images = [];
          
          for (let i = 1; i <= numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({scale: 1.5});
            
            // Create canvas
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            
            // Render PDF page into canvas context
            await page.render({
              canvasContext: context,
              viewport: viewport
            }).promise;
            
            // Convert canvas to data URL
            const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
            images.push(dataUrl);
          }
          
          resolve(images);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = function(error) {
        reject(error);
      };
      
      reader.readAsArrayBuffer(pdfFile);
    });
  };

  const processImagesWithGPT4Vision = async (images) => {
    let allText = '';
    
    // For larger PDFs, process them in batches to avoid token limits
    const batchSize = 3; // Process 3 pages at a time
    
    for (let i = 0; i < images.length; i += batchSize) {
      const batch = images.slice(i, i + batchSize);
      
      // Create content for each image in the batch
      const imageContents = batch.map((image, index) => ({
        type: "image_url",
        image_url: {
          url: image,
          detail: "high"
        }
      }));
      
      // Create messages array with system and user messages
      const messages = [
        {
          role: "system",
          content: "You are a precise text extractor. Extract all text from the images of documents. Preserve paragraphs, line breaks, and formatting as much as possible. Include all dates, times, and other important details."
        },
        {
          role: "user",
          content: [
            {
              type: "text",
              text: "Extract all text from these document images. Keep all the original formatting and structure."
            },
            ...imageContents
          ]
        }
      ];
      
      // Call OpenAI API
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`
        },
        body: JSON.stringify({
          model: "gpt-4o",
          messages: messages,
          max_tokens: 4096
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`API Error: ${errorData.error?.message || response.statusText}`);
      }
      
      const data = await response.json();
      
      // Append extracted text
      if (data.choices && data.choices[0] && data.choices[0].message) {
        allText += data.choices[0].message.content + "\n\n";
      }
      
      // Update progress
      setProgress(40 + Math.floor((i + batch.length) / images.length * 50));
    }
    
    return allText;
  };

  /* REMOVED STYLES - Eliminated container and header for cleaner integration with Upload page */
  /* Component now renders inline without its own container */

  const fileInputContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '1rem',
    width: '100%'
  };

  const fileInputStyle = {
    display: 'none' // Hide the default file input
  };

  const customFileButtonStyle = {
    padding: '0.8rem 1.5rem',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    border: '1px dashed var(--border-color)',
    borderRadius: '6px',
    fontSize: '1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    transition: 'all 0.3s ease'
  };

  const fileInfoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.5rem',
    fontSize: '0.9rem',
    color: 'var(--text-color)'
  };

  const extractButtonStyle = {
    padding: '0.8rem 1.5rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.3s ease',
    alignSelf: 'flex-start',
    minWidth: '180px',
    marginBottom: '15px'  // Added bottom margin
  };

  const progressBarContainerStyle = {
    width: '100%',
    height: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
    overflow: 'hidden',
    marginTop: '1rem'
  };

  const progressBarStyle = {
    height: '100%',
    backgroundColor: 'var(--primary-color)',
    width: `${progress}%`,
    transition: 'width 0.3s ease'
  };

  /* FILE TYPE ICONS - Returns PDF or image icon based on uploaded file type */
  /* Provides visual feedback about the file being processed */
  const getFileIcon = () => {
    if (!file) return null;
    
    if (file.type === 'application/pdf') {
      return (
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
        </svg>
      );
    } else if (file.type.startsWith('image/')) {
      return (
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
      );
    }
    
    return null;
  };

  /* FILE UPLOAD UI - Main upload interface with progress and status */
  /* Shows upload button, file info, extraction progress bar */
  return (
    <div style={fileInputContainerStyle}>
        <input
          type="file"
          accept="application/pdf,image/*"
          onChange={handleFileChange}
          style={fileInputStyle}
          ref={fileInputRef}
          disabled={isLoading}
        />
        
        <label 
          htmlFor="document-upload" 
          style={customFileButtonStyle}
          onClick={() => fileInputRef.current.click()}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            style={{ marginRight: '8px' }}
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          {file ? 'Change file' : 'Choose a PDF or image file'}
        </label>
        
        {file && (
          <div style={fileInfoStyle}>
            {getFileIcon()}
            {file.name} ({(file.size / 1024).toFixed(1)} KB) - {file.type.startsWith('image/') ? 'Image' : 'PDF'}
          </div>
        )}
        
        <button
          style={{
            ...extractButtonStyle,
            backgroundColor: isLoading ? 'var(--primary-hover)' : 'var(--primary-color)',
            opacity: (!file || isLoading) ? 0.6 : 1,
            cursor: (!file || isLoading) ? 'not-allowed' : 'pointer'
          }}
          onClick={extractTextFromDocument}
          disabled={!file || isLoading}
        >
          {isLoading ? (
            <>
              <span className="button-spinner"></span>
              <span style={{ marginLeft: '8px' }}>Scanning...</span>
            </>
          ) : (
            <>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ marginRight: '8px' }}
              >
                <polyline points="9 11 12 14 22 4"></polyline>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
              </svg>
              Extract Text
            </>
          )}
        </button>
        
        {isLoading && (
          <div style={progressBarContainerStyle}>
            <div style={progressBarStyle}></div>
          </div>
        )}
        
        {error && <div className="error">{error}</div>}
      </div>
  );
};

export default DocumentScanner;